import React, { useEffect, useRef, useState } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import Editor from "react-simple-code-editor"
import Prism from "prismjs"
import "prismjs/components/prism-javascript"
import "prismjs/components/prism-python"
import "prismjs/components/prism-java"
import "prismjs/themes/prism-tomorrow.css"
import "./index.css"
import Swal from "sweetalert2"
import { fetchNodeData, FetchNodePostData } from "utils/common-fetchData"

const LANGUAGES = [
  { label: "JavaScript", value: "javascript" },
  { label: "Python", value: "python" },
  { label: "Java", value: "java" },
]

const PRISM_MAP = {
  javascript: Prism.languages.javascript,
  python: Prism.languages.python,
  java: Prism.languages.java,
}

function formatTime(sec) {
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = sec % 60
  if (h > 0)
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
}

function isAnsweredFn(answers, id) {
  const v = answers[id]
  return v !== undefined && v !== null && v.toString().trim() !== ""
}

// Parses "mm:ss" or plain integer duration → seconds
function parseDuration(dur) {
  if (!dur) return 60
  const str = String(dur)
  if (str.includes(":")) {
    const [m, s] = str.split(":").map(Number)
    const total = (m || 0) * 60 + (s || 0)
    return total > 0 ? total : 60
  }
  return parseInt(str) || 60
}

// Reverses encodeTestId from AssessmentExam/index.js
const _TOKEN_SECRET = 1513948082
const _USER_KEY = "zt9x2k7r"

function decodeUserData(encoded) {
  try {
    const padded = encoded.replace(/-/g, "+").replace(/_/g, "/")
    const padding = (4 - (padded.length % 4)) % 4
    const raw = atob(padded + "=".repeat(padding))
    let json = ""
    for (let i = 0; i < raw.length; i++) {
      json += String.fromCharCode(raw.charCodeAt(i) ^ _USER_KEY.charCodeAt(i % _USER_KEY.length))
    }
    return JSON.parse(json)
  } catch {
    return { name: "", email: "" }
  }
}

function decodeTestId(token) {
  if (!token || token.length < 16) return null
  const hex = token.slice(8)
  const decoded = (parseInt(hex, 16) ^ _TOKEN_SECRET) >>> 0
  return isNaN(decoded) ? null : String(decoded)
}

// ─── Intro Screen ─────────────────────────────────────────────────────────────
function IntroScreen({ candidate, onCandidateChange, onStart, examTitle, totalQuestions, isInternal }) {
  return (
    <div className="ep-intro">
      <div className="ep-intro-card">
        <div className="ep-intro-header">
          <div className="ep-intro-icon">
            <i className="fas fa-graduation-cap"></i>
          </div>
          <h1>{examTitle || "Assessment Exam"}</h1>
          <p>Carefully read the instructions before you begin.</p>
        </div>

        <div className="ep-intro-body">
          <div className="ep-instructions">
            <h3>
              <i className="fas fa-list-ul me-2"></i>Instructions
            </h3>
            <ul>
              <li>
                This exam has <b>{totalQuestions} questions</b>. Each
                question has its <b>own countdown timer</b>.
              </li>
              <li>
                Question types: <b>Multiple Choice</b>, <b>Text Answer</b>, and{" "}
                <b>Code Editor</b>.
              </li>
              <li>
                When a question timer expires, it <b>auto-advances</b> to the
                next question.
              </li>
              <li>
                The timer starts immediately when you click <b>Start Exam</b>.
              </li>
              <li>
                Copy &amp; paste is <b>disabled</b> in text and code sections.
              </li>
              <li>
                Do <b>NOT</b> refresh or close the browser tab during the exam.
              </li>
              <li>
                The exam auto-submits when the timer reaches <b>00:00</b>.
              </li>
              <li>
                Make sure your personal details below are <b>correct</b>.
              </li>
            </ul>
          </div>

          <div className="ep-candidate-form">
            <h3>
              <i className="fas fa-user me-2"></i>Your Details
              {isInternal && (
                <span style={{ fontSize: 12, fontWeight: 400, color: "#752a97", marginLeft: 10 }}>
                  <i className="fas fa-lock me-1"></i>Auto-filled from your account
                </span>
              )}
            </h3>
            <div className="ep-form-grid">
              <div className="ep-field">
                <label>
                  Full Name <span className="ep-required">*</span>
                </label>
                <input
                  type="text"
                  name="fullName"
                  placeholder="Enter your full name"
                  value={candidate.fullName}
                  onChange={onCandidateChange}
                  readOnly={isInternal}
                  autoComplete="off"
                  style={isInternal ? { background: "#f5f0ff", cursor: "not-allowed", color: "#555" } : {}}
                />
              </div>
              <div className="ep-field">
                <label>
                  Email Address <span className="ep-required">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  value={candidate.email}
                  onChange={onCandidateChange}
                  readOnly={isInternal}
                  autoComplete="off"
                  style={isInternal ? { background: "#f5f0ff", cursor: "not-allowed", color: "#555" } : {}}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="ep-intro-footer">
          <button className="ep-start-btn" onClick={onStart}>
            <i className="fas fa-play-circle me-2"></i>Start Exam
          </button>
          <p className="ep-intro-note">
            By starting, you agree to complete the exam honestly and without
            assistance.
          </p>
        </div>
      </div>
    </div>
  )
}

// ─── Proctoring Hook ──────────────────────────────────────────────────────────
function useProctoring(active, currentQRef) {
  const [violations, setViolations] = useState([])
  const [showWarning, setShowWarning] = useState(false)
  const [lastViolation, setLastViolation] = useState(null)
  const lastLeaveTs = useRef(null)   // timestamp of the most recent leave event
  const hasLeft = useRef(false)      // prevents recording resume on initial focus

  const LEAVE_LABELS = {
    tab_switch:      "Tab switch (another browser tab)",
    window_blur:     "App / window switch",
    fullscreen_exit: "Fullscreen exit",
  }

  useEffect(() => {
    if (!active) return

    // Fullscreen is requested before this hook activates (in handleAgreeAndStart)

    let blurTimer = null

    const recordLeave = type => {
      const now = Date.now()
      lastLeaveTs.current = now
      hasLeft.current = true
      const v = {
        kind: "leave",
        type,
        time: new Date(now).toLocaleTimeString(),
        timestamp: now,
        label: LEAVE_LABELS[type] ?? type,
        question_id: currentQRef?.current?.id ?? null,
        question_index: currentQRef?.current?.index ?? null,
      }
      setLastViolation(v)
      setShowWarning(true)
      setViolations(prev => [...prev, v])
    }

    const recordResume = () => {
      if (!hasLeft.current) return   // no leave recorded yet — skip initial focus
      hasLeft.current = false        // reset immediately so concurrent events don't double-fire
      const now = Date.now()
      const durationSec = lastLeaveTs.current
        ? Math.round((now - lastLeaveTs.current) / 1000)
        : null
      lastLeaveTs.current = null
      const v = {
        kind: "resume",
        type: "exam_resumed",
        time: new Date(now).toLocaleTimeString(),
        timestamp: now,
        label: "Exam resumed",
        durationAway: durationSec,
        question_id: currentQRef?.current?.id ?? null,
        question_index: currentQRef?.current?.index ?? null,
      }
      setViolations(prev => [...prev, v])
    }

    const handleKeyDown = e => {
      if (e.key === "F12") { e.preventDefault(); return }
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && ["i","c","j","k"].includes(e.key.toLowerCase())) { e.preventDefault(); return }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "u") { e.preventDefault(); return }
    }

    const blockMenu = e => e.preventDefault()

    const handleVisibility = () => {
      if (document.hidden) {
        clearTimeout(blurTimer)
        recordLeave("tab_switch")
      } else {
        recordResume()
      }
    }

    // Debounced blur — covers Alt+Tab / switching to another app
    const handleBlur = () => {
      if (document.hidden) return
      blurTimer = setTimeout(() => {
        if (!document.hasFocus()) recordLeave("window_blur")
      }, 300)
    }

    const handleFocus = () => {
      clearTimeout(blurTimer)
      recordResume()
    }

    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        recordLeave("fullscreen_exit")
      } else {
        recordResume()
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    document.addEventListener("contextmenu", blockMenu)
    document.addEventListener("visibilitychange", handleVisibility)
    window.addEventListener("blur", handleBlur)
    window.addEventListener("focus", handleFocus)
    document.addEventListener("fullscreenchange", handleFullscreenChange)

    return () => {
      clearTimeout(blurTimer)
      document.removeEventListener("keydown", handleKeyDown)
      document.removeEventListener("contextmenu", blockMenu)
      document.removeEventListener("visibilitychange", handleVisibility)
      window.removeEventListener("blur", handleBlur)
      window.removeEventListener("focus", handleFocus)
      document.removeEventListener("fullscreenchange", handleFullscreenChange)
      if (document.fullscreenElement) document.exitFullscreen?.()
    }
  }, [active])

  const returnToExam = () => {
    setShowWarning(false)
    document.documentElement.requestFullscreen?.().catch(() => {})
  }

  return { violations, showWarning, lastViolation, returnToExam }
}

// ─── Violation Overlay ────────────────────────────────────────────────────────
function ViolationOverlay({ violation, totalCount, onReturn }) {
  return (
    <div className="ep-vio-overlay">
      <div className="ep-vio-card">
        <div className="ep-vio-icon">
          <i className="fas fa-exclamation-triangle"></i>
        </div>
        <h2 className="ep-vio-title">Proctoring Alert</h2>
        <p className="ep-vio-event">
          <b>{violation?.label}</b> detected at <b>{violation?.time}</b>
        </p>
        <div className="ep-vio-count-badge">
          <i className="fas fa-history me-1"></i>
          {totalCount} violation{totalCount !== 1 ? "s" : ""} recorded this session
        </div>
        <p className="ep-vio-body">
          Leaving the exam window is not permitted. This activity has been
          timestamped and will be reported to the administrator along with your
          submission.
        </p>
        <button className="ep-vio-btn" onClick={onReturn}>
          <i className="fas fa-arrow-left me-2"></i>Return to Exam
        </button>
        <p className="ep-vio-note">
          <i className="fas fa-shield-alt me-1"></i>All focus changes are
          logged with exact timestamps during this proctored session.
        </p>
      </div>
    </div>
  )
}

// ─── Exam Screen (one question at a time, per-question timer) ────────────────
function ExamScreen({ questions, answers, currentIndex, onAnswer, onNavigate, onSubmit, candidate, onViolation, paused, examTitle }) {
  const q = questions[currentIndex] ?? questions[0]
  const isLast = currentIndex === questions.length - 1
  const [qTimeLeft, setQTimeLeft] = useState(q?.timeLimit ?? 60)
  const [language, setLanguage] = useState("javascript")
  const timerRef = useRef(null)
  const timeSpentRef = useRef({}) // tracks seconds used per question id

  // keeps track of which question is active at violation time
  const currentQRef = useRef({ id: questions[currentIndex]?.id, index: currentIndex + 1 })
  useEffect(() => {
    currentQRef.current = { id: questions[currentIndex]?.id, index: currentIndex + 1 }
  }, [currentIndex])

  const { violations, showWarning, lastViolation, returnToExam } = useProctoring(true, currentQRef)
  const answeredCount = questions.filter(qq => isAnsweredFn(answers, qq.id)).length

  // Propagate latest violation log to root for submission
  useEffect(() => {
    if (violations.length > 0) onViolation(violations)
  }, [violations]) // eslint-disable-line

  // Start/pause timer when question changes, paused flag changes, or violation overlay toggles
  useEffect(() => {
    clearInterval(timerRef.current)

    const qId = questions[currentIndex]?.id
    const limit = questions[currentIndex]?.timeLimit ?? 60
    const spent = timeSpentRef.current[qId] || 0
    const remaining = Math.max(0, limit - spent)

    setQTimeLeft(remaining)

    // Don't run timer while rules modal open, violation overlay showing, or time already up
    if (paused || showWarning || remaining === 0) return

    timerRef.current = setInterval(() => {
      setQTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current)
          timeSpentRef.current[qId] = limit
          if (currentIndex < questions.length - 1) {
            onNavigate(currentIndex + 1)
          } else {
            onSubmit()
          }
          return 0
        }
        timeSpentRef.current[qId] = (timeSpentRef.current[qId] || 0) + 1
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timerRef.current)
  }, [currentIndex, paused, showWarning]) // eslint-disable-line

  const blockCopyPaste = e => {
    if ((e.ctrlKey || e.metaKey) && ["c", "v", "x"].includes(e.key.toLowerCase()))
      e.preventDefault()
  }
  const blockClipboard = e => e.preventDefault()

  const pct = qTimeLeft / (q?.timeLimit ?? 60)
  const timerCls = `ep-timer${pct <= 0.25 ? " ep-timer-danger" : pct <= 0.5 ? " ep-timer-warning" : ""}`

  return (
    <div className="ep-exam">
      {/* Violation overlay — shown whenever a focus-loss event is detected */}
      {showWarning && (
        <ViolationOverlay
          violation={lastViolation}
          totalCount={violations.length}
          onReturn={returnToExam}
        />
      )}

      {/* ── Topbar ── */}
      <div className="ep-topbar">
        <div className="ep-topbar-left">
          <div className="ep-topbar-logo">
            <i className="fas fa-graduation-cap"></i>
          </div>
          <div className="ep-topbar-info">
            <div className="ep-topbar-title">{examTitle || "Assessment"}</div>
            <div className="ep-topbar-name">
              <i className="fas fa-user me-1"></i>{candidate.fullName}
            </div>
          </div>
        </div>

        <div className="ep-topbar-center">
          <div className="ep-progress-track">
            <div className="ep-progress-fill" style={{ width: `${(answeredCount / questions.length) * 100}%` }} />
          </div>
          <div className="ep-progress-label">{answeredCount} / {questions.length} answered</div>
        </div>

        <div className="ep-topbar-right">
          {violations.length > 0 && (
            <div className="ep-vio-topbar-badge" title="Violations recorded">
              <i className="fas fa-eye me-1"></i>{violations.length} violation{violations.length !== 1 ? "s" : ""}
            </div>
          )}
          <div className={timerCls}>
            <i className="fas fa-hourglass-half me-2"></i>{formatTime(qTimeLeft)}
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="ep-body">
        {/* Sidebar */}
        <div className="ep-sidebar">
          <div className="ep-sidebar-head">Question Palette</div>
          <div className="ep-qdot-grid">
            {questions.map((qq, idx) => {
              const isAnswered = isAnsweredFn(answers, qq.id)
              const isCurrent = idx === currentIndex
              const isPast = idx < currentIndex
              let cls = "ep-qdot"
              if (isCurrent) cls += " ep-qdot-current"
              else if (isPast) cls += " ep-qdot-past"
              else if (isAnswered) cls += " ep-qdot-answered"
              else cls += " ep-qdot-unvisited"
              return (
                <button
                  key={qq.id}
                  className={cls}
                  onClick={() => !isPast && onNavigate(idx)}
                  disabled={isPast}
                  style={isPast ? { cursor: "not-allowed", opacity: 0.45 } : {}}
                >
                  {idx + 1}
                </button>
              )
            })}
          </div>
          <div className="ep-legend">
            <div className="ep-legend-row"><span className="ep-legend-dot ep-qdot-current"></span>Current</div>
            <div className="ep-legend-row"><span className="ep-legend-dot ep-qdot-answered"></span>Answered</div>
            <div className="ep-legend-row"><span className="ep-legend-dot ep-qdot-unvisited"></span>Pending</div>
          </div>
          <div className="ep-sidebar-stats">
            <div className="ep-stat-row"><span>Total</span><b>{questions.length}</b></div>
            <div className="ep-stat-row"><span>Answered</span><b className="text-success">{answeredCount}</b></div>
            <div className="ep-stat-row"><span>Remaining</span><b className="text-danger">{questions.length - answeredCount}</b></div>
          </div>
        </div>

        {/* Main — single question */}
        <div className="ep-main">
          <div className="ep-q-section">
            {/* Meta bar */}
            <div className="ep-q-meta-bar">
              <span className="ep-q-num-badge">
                Q {currentIndex + 1} <span className="ep-q-of">/ {questions.length}</span>
              </span>
              <span className="ep-q-section-badge">{q.section}</span>
              <span className={`ep-q-type-badge ep-q-type-${q.type}`}>
                {q.type === "mcq" ? "Multiple Choice" : q.type === "input" ? "Text Answer" : "Code"}
              </span>
            </div>

            {/* Question text */}
            <div className="ep-q-text">
              {q.question.split("\n").map((line, i) => (
                <p key={i} className={i === 0 ? "ep-q-main" : "ep-q-hint"}>{line}</p>
              ))}
            </div>

            {/* Answer area */}
            <div className="ep-answer-area">
              {q.type === "mcq" && (
                <div className="ep-options">
                  {q.options.map((opt, i) => {
                    const optIndex = String(i + 1)
                    const selected = answers[q.id] === optIndex
                    return (
                      <label
                        key={i}
                        className={`ep-option${selected ? " ep-option-selected" : ""}`}
                        onClick={() => onAnswer(q.id, optIndex)}
                      >
                        <span className="ep-option-letter">{String.fromCharCode(65 + i)}</span>
                        <input type="radio" name={`q-${q.id}`} checked={selected} onChange={() => {}} className="d-none" />
                        <span className="ep-option-text">{opt}</span>
                        {selected && <i className="fas fa-check-circle ep-option-check"></i>}
                      </label>
                    )
                  })}
                </div>
              )}

              {q.type === "input" && (
                <div className="ep-textarea-wrap">
                  <div className="ep-textarea-label"><i className="fas fa-pen-nib me-2"></i>Write your answer below</div>
                  <textarea
                    className="ep-textarea"
                    rows={9}
                    placeholder="Type your detailed answer here..."
                    value={answers[q.id] || ""}
                    onChange={e => onAnswer(q.id, e.target.value)}
                    onKeyDown={blockCopyPaste}
                    onCopy={blockClipboard}
                    onPaste={blockClipboard}
                    onCut={blockClipboard}
                    onContextMenu={blockClipboard}
                  />
                  <div className="ep-textarea-count">{(answers[q.id] || "").length} characters</div>
                </div>
              )}

              {q.type === "code" && (
                <div className="ep-code-wrap">
                  <div className="ep-code-topbar">
                    <div className="ep-code-dots">
                      <span className="ep-dot ep-dot-red"></span>
                      <span className="ep-dot ep-dot-yellow"></span>
                      <span className="ep-dot ep-dot-green"></span>
                    </div>
                    <span className="ep-code-label"><i className="fas fa-code me-2"></i>Code Editor</span>
                    <div className="ep-lang-wrap">
                      <select
                        className="ep-lang-select"
                        value={language}
                        onChange={e => setLanguage(e.target.value)}
                      >
                        {LANGUAGES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="ep-code-body">
                    <Editor
                      value={answers[q.id] || ""}
                      onValueChange={code => onAnswer(q.id, code)}
                      highlight={code => Prism.highlight(code, PRISM_MAP[language] || Prism.languages.javascript, language)}
                      padding={16}
                      textareaClassName="ep-code-textarea"
                      preClassName="ep-code-pre"
                      placeholder={`// Write your ${LANGUAGES.find(l => l.value === language)?.label} solution here...`}
                      style={{ fontFamily: '"Fira Code", "Cascadia Code", monospace', fontSize: 14, minHeight: "260px" }}
                      onKeyDown={blockCopyPaste}
                      onPaste={blockClipboard}
                      onCopy={blockClipboard}
                      onCut={blockClipboard}
                      onContextMenu={blockClipboard}
                    />
                  </div>
                  <div className="ep-code-footer">
                    <span className="text-muted" style={{ fontSize: 12 }}><i className="fas fa-lock me-1"></i>Copy &amp; paste disabled</span>
                    <span className="text-muted" style={{ fontSize: 12 }}>{(answers[q.id] || "").split("\n").length} lines</span>
                  </div>
                </div>
              )}
            </div>

            {/* Navigation — no Previous */}
            <div className="ep-nav-bar">
              <button
                className="ep-btn ep-btn-clear"
                onClick={() => onAnswer(q.id, "")}
                disabled={!isAnsweredFn(answers, q.id)}
              >
                <i className="fas fa-eraser me-2"></i>Clear
              </button>

              {isLast ? (
                <button className="ep-btn ep-btn-submit" onClick={onSubmit}>
                  <i className="fas fa-paper-plane me-2"></i>Submit Exam
                </button>
              ) : (
                <button className="ep-btn ep-btn-next" onClick={() => onNavigate(currentIndex + 1)}>
                  Next<i className="fas fa-arrow-right ms-2"></i>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Submitted Screen ─────────────────────────────────────────────────────────
function SubmittedScreen({ candidate, answeredCount, total }) {
  const skipped = total - answeredCount
  return (
    <div className="ep-submitted">
      <div className="ep-submitted-card">
        <div className="ep-submitted-check">
          <i className="fas fa-check"></i>
        </div>
        <h1>Exam Submitted!</h1>
        <p>
          Thank you, <b>{candidate.fullName}</b>. Your responses have been
          recorded successfully.
        </p>
        <div className="ep-submitted-stats">
          <div className="ep-submitted-stat ep-stat-answered">
            <div className="ep-stat-val">{answeredCount}</div>
            <div className="ep-stat-lbl">Answered</div>
          </div>
          <div className="ep-submitted-stat ep-stat-skipped">
            <div className="ep-stat-val">{skipped}</div>
            <div className="ep-stat-lbl">Skipped</div>
          </div>
          <div className="ep-submitted-stat ep-stat-total">
            <div className="ep-stat-val">{total}</div>
            <div className="ep-stat-lbl">Total</div>
          </div>
        </div>
        <p className="ep-submitted-note">
          Results will be communicated to <b>{candidate.email}</b>. You may now
          close this window.
        </p>
      </div>
    </div>
  )
}

// ─── Rules Modal ──────────────────────────────────────────────────────────────
const PROCTOR_RULES = [
  { icon: "fas fa-exchange-alt", color: "#e67e22", text: "Switching to another browser tab will be detected and recorded" },
  { icon: "fas fa-desktop",      color: "#e74c3c", text: "Switching to another application or OS window will be detected and recorded" },
  { icon: "fas fa-compress-alt", color: "#8e44ad", text: "Exiting fullscreen mode will be detected and recorded" },
  { icon: "fas fa-history",      color: "#c0392b", text: "All violations are timestamped and reported to the administrator with duration" },
]

const EXAM_RULES = [
  { icon: "fas fa-hourglass-half", text: "Each question has its own individual countdown timer" },
  { icon: "fas fa-forward",        text: "When a question timer expires, the exam auto-advances to the next question" },
  { icon: "fas fa-ban",            text: "Copy, paste, and right-click are disabled throughout the exam" },
  { icon: "fas fa-expand",         text: "The exam runs in fullscreen — exiting fullscreen is a violation" },
  { icon: "fas fa-pause-circle",   text: "You cannot pause or go back once the exam has started" },
]

function RulesModal({ isOpen, onClose, onAgree, examTitle, totalQuestions }) {
  if (!isOpen) return null
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
    }}>
      <div style={{
        background: "#fff", borderRadius: 16, width: "100%", maxWidth: 900,
        boxShadow: "0 24px 64px rgba(0,0,0,0.35)",
        animation: "ep-fadein .2s ease",
        display: "flex", flexDirection: "column",
      }}>
        {/* Header */}
        <div style={{
          background: "linear-gradient(135deg, #752a97 0%, #5c0882 100%)",
          borderRadius: "16px 16px 0 0", padding: "22px 28px 20px", color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ fontSize: 32 }}><i className="fas fa-shield-alt"></i></div>
            <div>
              <h2 style={{ margin: 0, fontSize: 19, fontWeight: 800, letterSpacing: -0.3 }}>
                Exam Rules &amp; Proctoring Policy
              </h2>
              {examTitle && (
                <p style={{ margin: "3px 0 0", opacity: 0.75, fontSize: 13 }}>
                  {examTitle} &nbsp;·&nbsp; {totalQuestions} questions
                </p>
              )}
            </div>
          </div>
          <p style={{ margin: 0, fontSize: 12, opacity: 0.7, maxWidth: 280, textAlign: "right", lineHeight: 1.5 }}>
            Please read carefully. By clicking <b>I Agree</b> you confirm you will comply with these rules.
          </p>
        </div>

        {/* Body — two columns, no scroll */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0 }}>
          {/* Left — Proctoring */}
          <div style={{
            padding: "22px 24px 20px",
            borderRight: "1px solid #f0f0f0",
            display: "flex", flexDirection: "column", gap: 10,
          }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 8, marginBottom: 4,
              color: "#c0392b", fontWeight: 700, fontSize: 11,
              textTransform: "uppercase", letterSpacing: 1,
            }}>
              <i className="fas fa-eye"></i> Proctoring — Your Activity Is Monitored
            </div>
            {PROCTOR_RULES.map((r, i) => (
              <div key={i} style={{
                display: "flex", alignItems: "flex-start", gap: 12,
                background: "#fff5f5", border: "1px solid #ffd5d5",
                borderRadius: 8, padding: "10px 14px",
              }}>
                <i className={r.icon} style={{ color: r.color, marginTop: 2, minWidth: 16, textAlign: "center" }}></i>
                <span style={{ fontSize: 13, color: "#444", lineHeight: 1.55 }}>{r.text}</span>
              </div>
            ))}
            <div style={{
              background: "#fff5f5", border: "1px solid #ffd5d5",
              borderRadius: 8, padding: "8px 12px",
              fontSize: 11.5, color: "#888", lineHeight: 1.5, marginTop: 2,
            }}>
              <i className="fas fa-info-circle" style={{ marginRight: 6, color: "#e74c3c" }}></i>
              Browser security limits app detection to tab switch vs. window switch only.
            </div>
          </div>

          {/* Right — Exam Rules */}
          <div style={{
            padding: "22px 24px 20px",
            display: "flex", flexDirection: "column", gap: 10,
          }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 8, marginBottom: 4,
              color: "#752a97", fontWeight: 700, fontSize: 11,
              textTransform: "uppercase", letterSpacing: 1,
            }}>
              <i className="fas fa-list-ul"></i> Exam Rules
            </div>
            {EXAM_RULES.map((r, i) => (
              <div key={i} style={{
                display: "flex", alignItems: "flex-start", gap: 12,
                background: "#f9f3ff", border: "1px solid #e5d0f5",
                borderRadius: 8, padding: "10px 14px",
              }}>
                <i className={r.icon} style={{ color: "#752a97", marginTop: 2, minWidth: 16, textAlign: "center" }}></i>
                <span style={{ fontSize: 13, color: "#444", lineHeight: 1.55 }}>{r.text}</span>
              </div>
            ))}
            <div style={{
              background: "#f9f3ff", border: "1px solid #e5d0f5",
              borderRadius: 8, padding: "8px 12px",
              fontSize: 11.5, color: "#888", lineHeight: 1.5, marginTop: 2,
            }}>
              <i className="fas fa-lock" style={{ marginRight: 6, color: "#752a97" }}></i>
              Once started, you cannot pause or restart. Ensure you are in a quiet environment.
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: "14px 24px 20px", borderTop: "1px solid #f0f0f0",
          display: "flex", gap: 12, justifyContent: "flex-end",
        }}>
          <button
            onClick={onClose}
            style={{
              padding: "10px 22px", borderRadius: 8,
              border: "1.5px solid #ddd", background: "#fff",
              color: "#666", fontSize: 14, fontWeight: 600, cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={onAgree}
            style={{
              padding: "10px 28px", borderRadius: 8, border: "none",
              background: "linear-gradient(135deg, #752a97 0%, #5c0882 100%)",
              color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer",
              boxShadow: "0 4px 14px rgba(117,42,151,0.45)",
              display: "flex", alignItems: "center", gap: 8,
            }}
          >
            <i className="fas fa-play-circle"></i> I Agree, Start Exam
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Root ─────────────────────────────────────────────────────────────────────
function AssessmentFinalForm() {
  const { id: token } = useParams()
  const [searchParams] = useSearchParams()
  const testId = decodeTestId(token)

  const d = searchParams.get("d") || ""
  const { name: prefillName, email: prefillEmail } = d ? decodeUserData(d) : { name: "", email: "" }
  const isInternal = Boolean(prefillName && prefillEmail)

  const [phase, setPhase] = useState("intro")
  const [showRules, setShowRules] = useState(false)
  const [candidate, setCandidate] = useState({ fullName: prefillName, email: prefillEmail })
  const [answers, setAnswers] = useState({})
  const [currentIndex, setCurrentIndex] = useState(0)
  const [violations, setViolations] = useState([])
  const [questions, setQuestions] = useState([])
  const [examInfo, setExamInfo] = useState(null)
  const [examLoading, setExamLoading] = useState(true)
  const [examError, setExamError] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!testId) {
      setExamError("Invalid exam link. Please check the URL and try again.")
      setExamLoading(false)
      return
    }
    fetchNodeData(null, `preassesment/test/getAssessmentData/${testId}`, null)
      .then(res => {
        if (!res?.success) {
          setExamError(res?.message || "Failed to load exam.")
          setExamLoading(false)
          return
        }
        setExamInfo(res.data.test)
        // DB type: 1 = MCQ, 2 = Text/Descriptive, 3 = Code
        const typeMap = { 1: "mcq", 2: "input", 3: "code" }
        const transformed = (res.data.questions || []).map(q => {
          let options = []
          if (q.type === 1 && q.options) {
            try { options = Object.values(JSON.parse(q.options)) } catch {}
          }
          return {
            id: q.id,
            type: typeMap[q.type] ?? "input",
            question: q.question,
            options,
            section: "",
            timeLimit: parseDuration(q.duration),
          }
        })
        if (transformed.length === 0) {
          setExamError("No questions are available for this exam yet. Please contact the administrator.")
          setExamLoading(false)
          return
        }
        setQuestions(transformed)
        setExamLoading(false)
      })
      .catch(() => {
        setExamError("Failed to load exam. Please try again.")
        setExamLoading(false)
      })
  }, [testId])

  const handleCandidateChange = e => {
    const { name, value } = e.target
    setCandidate(prev => ({ ...prev, [name]: value }))
  }

  const handleStart = () => {
    if (!candidate.fullName.trim() || !candidate.email.trim()) {
      Swal.fire({ icon: "warning", title: "Details required", text: "Please enter your name and email before starting the exam." })
      return
    }
    if (questions.length === 0) {
      Swal.fire({ icon: "error", title: "No Questions", text: "This exam has no questions configured. Please contact the administrator." })
      return
    }
    document.documentElement.requestFullscreen?.().catch(() => {})
    setPhase("exam")
    setShowRules(true)
  }

  const handleAgreeAndStart = () => {
    setShowRules(false)
  }

  const handleAnswer = (id, value) => {
    setAnswers(prev => ({ ...prev, [id]: value }))
  }

  const doSubmit = async () => {
    setSubmitting(true)
    // Send DB type numbers so backend can match correctly: 1=MCQ, 2=text, 3=code
    const submitQuestions = questions.map(q => ({
      id: q.id,
      type: q.type === "mcq" ? 1 : q.type === "input" ? 2 : 3,
      answer: answers[q.id] ?? "",
    }))
    try {
      await FetchNodePostData(null, "preassesment/test/submit", null, {
        details: { name: candidate.fullName, email: candidate.email },
        questions: submitQuestions,
        testId: Number(testId),
        violations,
      })
    } catch {}
    setSubmitting(false)
    setPhase("submitted")
  }

  const handleSubmit = () => {
    const answered = questions.filter(q => isAnsweredFn(answers, q.id)).length
    const unanswered = questions.length - answered
    Swal.fire({
      title: "Submit Exam?",
      html: `You have answered <b>${answered}</b> of <b>${questions.length}</b> questions.${
        unanswered > 0
          ? `<br/><span style="color:#e74c3c">${unanswered} question(s) left unanswered.</span>`
          : "<br/><span style='color:#27ae60'>All questions answered!</span>"
      }`,
      icon: unanswered > 0 ? "warning" : "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Submit",
      cancelButtonText: "Review",
      confirmButtonColor: "#752a97",
      cancelButtonColor: "#6c757d",
    }).then(res => { if (res.isConfirmed) doSubmit() })
  }

  const answeredCount = questions.filter(q => isAnsweredFn(answers, q.id)).length

  if (examLoading)
    return (
      <div className="ep-intro" style={{ flexDirection: "column", gap: 16 }}>
        <div style={{ fontSize: 40, color: "#752a97" }}><i className="fas fa-spinner fa-spin"></i></div>
        <p style={{ color: "#555", fontFamily: "Inter, sans-serif" }}>Loading exam...</p>
      </div>
    )

  if (examError)
    return (
      <div className="ep-intro" style={{ flexDirection: "column", gap: 16 }}>
        <div style={{ fontSize: 40, color: "#e74c3c" }}><i className="fas fa-exclamation-circle"></i></div>
        <p style={{ color: "#e74c3c", fontFamily: "Inter, sans-serif", fontWeight: 600 }}>{examError}</p>
      </div>
    )

  if (submitting)
    return (
      <div className="ep-intro" style={{ flexDirection: "column", gap: 16 }}>
        <div style={{ fontSize: 40, color: "#752a97" }}><i className="fas fa-spinner fa-spin"></i></div>
        <p style={{ color: "#555", fontFamily: "Inter, sans-serif" }}>Submitting your exam...</p>
      </div>
    )

  if (phase === "intro")
    return (
      <IntroScreen
        candidate={candidate}
        onCandidateChange={handleCandidateChange}
        onStart={handleStart}
        examTitle={examInfo?.test_name}
        totalQuestions={questions.length}
        isInternal={isInternal}
      />
    )

  if (phase === "submitted")
    return (
      <SubmittedScreen
        candidate={candidate}
        answeredCount={answeredCount}
        total={questions.length}
      />
    )

  return (
    <>
      <ExamScreen
        questions={questions}
        answers={answers}
        currentIndex={currentIndex}
        onAnswer={handleAnswer}
        onNavigate={setCurrentIndex}
        onSubmit={handleSubmit}
        candidate={candidate}
        onViolation={setViolations}
        paused={showRules}
        examTitle={examInfo?.test_name}
      />
      <RulesModal
        isOpen={showRules}
        onClose={() => setShowRules(false)}
        onAgree={handleAgreeAndStart}
        examTitle={examInfo?.test_name}
        totalQuestions={questions.length}
      />
    </>
  )
}

export default AssessmentFinalForm
